<template>
  <div class="layout-px-spacing">
    <div class="mi">
      <header class="mi-head">
        <h1 class="mi-title">Medical invoices</h1>
        <p class="mi-lede">
          One senior's medical invoices by issue date, split by category and
          ready to hand to finance.
        </p>
      </header>

      <form
        class="mi-panel mi-filters"
        @submit.prevent="loadReport"
        aria-label="Choose senior and period"
      >
        <div class="mi-field mi-field--senior">
          <label class="mi-label" for="mi-senior">Senior</label>
          <v-select
            ref="seniorSelect"
            input-id="mi-senior"
            v-model="senior"
            :options="seniorOptions"
            :filterable="false"
            :clearable="true"
            label="name"
            placeholder="Search by name"
            @search="onSeniorSearch"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
                aria-label="Senior"
              />
            </template>
            <template #no-options="{ search }">
              <span v-if="seniorSearchError">{{ seniorSearchError }}</span>
              <span v-else-if="!search || search.length < 2"
                >Type at least 2 letters to search.</span
              >
              <span v-else-if="seniorSearching">Searching…</span>
              <span v-else
                >No senior with invoices matches "{{ search }}".</span
              >
            </template>
            <template #option="option">
              <div class="mi-option">
                <span class="mi-option-name">{{ option.name }}</span>
                <span class="mi-option-meta">
                  Patient #{{ option.patient_id }} · Sponsor
                  {{ option.sponsor_name || "not recorded" }} ·
                  {{ option.invoices }} invoice{{
                    option.invoices === 1 ? "" : "s"
                  }}
                  · last {{ formatDate(option.last_issued_on) }}
                </span>
              </div>
            </template>
            <template #selected-option="option">
              <span
                >{{ option.name }}
                <span class="mi-muted"
                  >· Patient #{{ option.patient_id }}</span
                ></span
              >
            </template>
          </v-select>
        </div>

        <fieldset class="mi-field mi-field--period">
          <legend class="mi-label">Issue date</legend>
          <div class="mi-presets" role="group" aria-label="Quick periods">
            <button
              v-for="preset in presets"
              :key="preset.key"
              type="button"
              class="mi-chip"
              :class="{ 'is-active': activePreset === preset.key }"
              :aria-pressed="activePreset === preset.key ? 'true' : 'false'"
              @click="applyPreset(preset.key)"
            >
              {{ preset.label }}
            </button>
          </div>
          <div class="mi-dates">
            <label class="mi-date">
              <span class="mi-sublabel">From</span>
              <input
                class="mi-input"
                type="date"
                v-model="from"
                :max="to || today"
                required
                @input="activePreset = null"
              />
            </label>
            <label class="mi-date">
              <span class="mi-sublabel">To</span>
              <input
                class="mi-input"
                type="date"
                v-model="to"
                :min="from"
                :max="today"
                required
                @input="activePreset = null"
              />
            </label>
          </div>
        </fieldset>

        <div class="mi-field mi-field--action">
          <button
            type="submit"
            class="mi-btn mi-btn--primary"
            :disabled="!canLoad || loading"
          >
            {{ loading ? "Loading invoices…" : "Show invoices" }}
          </button>
          <p v-if="periodError" class="mi-error-text" role="alert">
            {{ periodError }}
          </p>
        </div>
      </form>

      <section
        v-if="error"
        class="mi-panel mi-state mi-state--error"
        role="alert"
      >
        <h2 class="mi-state-title">The invoices could not be loaded</h2>
        <p>{{ error }}</p>
        <button
          type="button"
          class="mi-btn mi-btn--secondary"
          @click="loadReport"
        >
          Try again
        </button>
      </section>

      <section
        v-else-if="loading"
        class="mi-panel mi-state"
        aria-live="polite"
        aria-busy="true"
      >
        <span class="mi-sr">Loading invoices…</span>
        <div v-for="n in 4" :key="n" class="mi-skeleton"></div>
      </section>

      <section v-else-if="!report" class="mi-panel mi-state">
        <h2 class="mi-state-title">Choose a senior and a period</h2>
        <p>
          You'll see every medical invoice issued to them in that period, totals
          by category, and possible re-issues. You can then download it as
          Excel, or as a zip with the invoice PDFs.
        </p>
      </section>

      <template v-else>
        <section class="mi-panel mi-report" aria-live="polite">
          <div class="mi-report-head">
            <div>
              <h2 class="mi-report-title">{{ report.senior.name }}</h2>
              <p class="mi-muted">
                Patient #{{ report.senior.patient_id }} · Sponsor
                {{ report.senior.sponsor_name || "not recorded" }} · issued
                {{ formatDate(report.period.from) }} –
                {{ formatDate(report.period.to) }}
              </p>
            </div>
            <div class="mi-downloads">
              <button
                type="button"
                class="mi-btn mi-btn--secondary"
                :disabled="!!downloading || !hasInvoices"
                @click="download('excel')"
              >
                {{
                  downloading === "excel"
                    ? "Preparing Excel…"
                    : "Download Excel"
                }}
              </button>
              <button
                type="button"
                class="mi-btn mi-btn--primary"
                :disabled="!!downloading || !hasInvoices"
                @click="download('bundle')"
              >
                {{
                  downloading === "bundle"
                    ? "Collecting PDFs…"
                    : "Download Excel + PDFs (.zip)"
                }}
              </button>
            </div>
          </div>
          <p v-if="downloadError" class="mi-error-text" role="alert">
            {{ downloadError }}
          </p>

          <div v-if="!hasInvoices" class="mi-state mi-state--inline">
            <h3 class="mi-state-title">No medical invoices in this period</h3>
            <p>
              No invoice was issued to {{ report.senior.name }} between
              {{ formatDate(report.period.from) }} and
              {{ formatDate(report.period.to) }}. Try a wider period.
            </p>
          </div>

          <div v-else class="mi-ledger-wrap">
            <table class="mi-ledger">
              <caption class="mi-sr">
                Totals by category. Select a category to filter the invoices
                below.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col" class="num">Invoices</th>
                  <th scope="col" class="num">Items</th>
                  <th scope="col" class="num">Delivery &amp; logistics</th>
                  <th scope="col" class="num">Total as billed</th>
                  <th scope="col" class="num">Excluding possible re-issues</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="category in categoriesWithInvoices"
                  :key="category.key"
                  :class="{ 'is-selected': filter === category.key }"
                >
                  <th scope="row">
                    <button
                      type="button"
                      class="mi-link"
                      :aria-pressed="filter === category.key ? 'true' : 'false'"
                      @click="toggleFilter(category.key)"
                    >
                      {{ category.label }}
                    </button>
                  </th>
                  <td class="num">{{ category.invoices }}</td>
                  <td class="num">{{ naira(category.items_charged) }}</td>
                  <td class="num">{{ naira(category.logistics_charged) }}</td>
                  <td class="num">{{ naira(category.as_billed) }}</td>
                  <td class="num">
                    {{ naira(category.as_billed_excluding_reissues) }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">All categories</th>
                  <td class="num">{{ report.totals.invoices }}</td>
                  <td></td>
                  <td></td>
                  <td class="num mi-grand">
                    {{ naira(report.totals.as_billed) }}
                  </td>
                  <td class="num">
                    {{ naira(report.totals.as_billed_excluding_reissues) }}
                  </td>
                </tr>
              </tfoot>
            </table>
            <p v-if="report.totals.possible_reissues" class="mi-reissue-note">
              <span class="mi-badge mi-badge--watch"
                >{{ report.totals.possible_reissues }} possible re-issue{{
                  report.totals.possible_reissues === 1 ? "" : "s"
                }}</span
              >
              These invoices repeat an earlier invoice's items and total in this
              period. Check them before counting both.
            </p>
          </div>
        </section>

        <section v-if="hasInvoices" class="mi-panel">
          <div class="mi-table-head">
            <h2 class="mi-section-title">Invoices</h2>
            <div
              class="mi-presets"
              role="group"
              aria-label="Filter invoices by category"
            >
              <button
                type="button"
                class="mi-chip"
                :class="{ 'is-active': !filter }"
                :aria-pressed="!filter ? 'true' : 'false'"
                @click="filter = null"
              >
                All
                <span class="mi-chip-count">{{ report.invoices.length }}</span>
              </button>
              <button
                v-for="category in categoriesWithInvoices"
                :key="category.key"
                type="button"
                class="mi-chip"
                :class="{ 'is-active': filter === category.key }"
                :aria-pressed="filter === category.key ? 'true' : 'false'"
                @click="toggleFilter(category.key)"
              >
                {{ category.label }}
                <span class="mi-chip-count">{{ category.invoices }}</span>
              </button>
            </div>
          </div>

          <div class="mi-table-wrap">
            <table class="mi-table">
              <caption class="mi-sr">
                Invoices, oldest first. Expand a row to see its line items.
              </caption>
              <thead>
                <tr>
                  <th scope="col"><span class="mi-sr">Line items</span></th>
                  <th scope="col">Issued</th>
                  <th scope="col">Invoice</th>
                  <th scope="col">Category</th>
                  <th scope="col">Vendor</th>
                  <th scope="col" class="num">Total as billed</th>
                  <th scope="col">Request payment</th>
                  <th scope="col">Item payments</th>
                  <th scope="col">Delivery</th>
                </tr>
              </thead>
              <tbody
                v-for="invoice in visibleInvoices"
                :key="invoice.invoice_no"
                :class="{ 'is-reissue': invoice.reissue_of }"
              >
                <tr>
                  <td>
                    <button
                      type="button"
                      class="mi-expand"
                      :aria-expanded="isOpen(invoice) ? 'true' : 'false'"
                      :aria-controls="'lines-' + invoice.invoice_no"
                      :aria-label="
                        (isOpen(invoice) ? 'Hide' : 'Show') +
                        ' line items for ' +
                        invoice.invoice_no
                      "
                      @click="toggleOpen(invoice)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        aria-hidden="true"
                        :class="{ 'is-open': isOpen(invoice) }"
                      >
                        <path
                          d="M9 6l6 6-6 6"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                  </td>
                  <td class="nowrap">{{ formatDate(invoice.issued_on) }}</td>
                  <td class="nowrap mono">
                    {{ invoice.invoice_no }}
                    <span
                      v-if="invoice.reissue_of"
                      class="mi-badge mi-badge--watch mi-badge--block"
                      >Possible re-issue of {{ invoice.reissue_of }}</span
                    >
                  </td>
                  <td>
                    {{ categoryLabel(invoice.primary_category) }}
                    <span v-if="invoice.is_mixed" class="mi-muted"
                      >(mixed)</span
                    >
                  </td>
                  <td>{{ invoice.vendors.join(", ") || "—" }}</td>
                  <td class="num">{{ naira(invoice.as_billed) }}</td>
                  <td>
                    <span
                      class="mi-badge"
                      :class="payClass(invoice.request_pay_status)"
                      >{{ payLabel(invoice.request_pay_status) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="mi-badge"
                      :class="payClass(invoice.items_pay_status)"
                      >{{ payLabel(invoice.items_pay_status) }}</span
                    >
                  </td>
                  <td>
                    {{
                      invoice.delivery_status === "delivered"
                        ? "Delivered"
                        : "Not delivered"
                    }}
                  </td>
                </tr>
                <tr
                  v-if="isOpen(invoice)"
                  :id="'lines-' + invoice.invoice_no"
                  class="mi-lines-row"
                >
                  <td></td>
                  <td colspan="8">
                    <table class="mi-lines">
                      <caption class="mi-sr">
                        Line items for
                        {{
                          invoice.invoice_no
                        }}
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">Item</th>
                          <th scope="col">Type</th>
                          <th scope="col">Vendor</th>
                          <th scope="col" class="num">Base</th>
                          <th scope="col" class="num">Charge</th>
                          <th scope="col" class="num">As billed</th>
                          <th scope="col">Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="line in invoice.lines" :key="line.item_id">
                          <td class="mi-desc">{{ line.description || "—" }}</td>
                          <td>
                            {{
                              line.is_logistics
                                ? "Delivery & logistics"
                                : categoryLabel(line.category)
                            }}
                          </td>
                          <td>{{ line.vendor || "—" }}</td>
                          <td class="num">{{ naira(line.base) }}</td>
                          <td class="num">
                            {{
                              line.charge_pct !== null
                                ? line.charge_pct + "%"
                                : "—"
                            }}
                          </td>
                          <td class="num">{{ naira(line.charged) }}</td>
                          <td>
                            <span
                              class="mi-badge"
                              :class="payClass(line.pay_status)"
                              >{{ payLabel(line.pay_status) }}</span
                            >
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <dl class="mi-notes">
            <div>
              <dt>Total as billed</dt>
              <dd>
                The total printed on the invoice PDF: vendor price, plus the
                Gerocare charge, plus delivery and logistics.
              </dd>
            </div>
            <div>
              <dt>Charge</dt>
              <dd>
                The percentage Gerocare adds to each line. The CRM labels this
                column "VAT".
              </dd>
            </div>
            <div>
              <dt>Two payment statuses</dt>
              <dd>
                The CRM records payment on the request and on each line item,
                and they can disagree. Both are shown.
              </dd>
            </div>
          </dl>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import debounce from "debounce";
import moment from "moment";
import { http } from "@/utils/http";
import { endpoints } from "@/utils/endpoints";

const CATEGORY_LABELS = {
  medications: "Medications",
  investigations: "Investigations",
  specialist: "Specialist",
  physiotherapy: "Physiotherapy",
  caregiver: "Caregiver",
  other: "Other",
  logistics: "Delivery & logistics",
};

const PAY_LABELS = {
  paid: "Paid",
  "paid not confirmed": "Paid, not confirmed",
  "not paid": "Not paid",
  mixed: "Mixed",
  "not recorded": "Not recorded",
};

/** Finance report of one senior's medical invoices with Excel and PDF bundle downloads. */
export default {
  name: "MedicalInvoices",

  data() {
    return {
      senior: null,
      seniorOptions: [],
      seniorSearching: false,
      seniorSearchError: null,
      from: null,
      to: null,
      activePreset: null,
      report: null,
      loading: false,
      error: null,
      filter: null,
      open: {},
      downloading: null,
      downloadError: null,
      presets: [
        { key: "thisMonth", label: "This month" },
        { key: "lastMonth", label: "Last month" },
        { key: "last3Months", label: "Last 3 months" },
        { key: "yearToDate", label: "Year to date" },
      ],
    };
  },

  computed: {
    today() {
      return moment().format("YYYY-MM-DD");
    },

    periodError() {
      if (this.from && this.to && this.from > this.to) {
        return "The start date must be on or before the end date.";
      }
      if (this.to && this.to > this.today) {
        return "The end date cannot be in the future.";
      }
      if (
        this.from &&
        this.to &&
        moment(this.to).diff(moment(this.from), "days") > 366
      ) {
        return "Choose a period of at most 366 days.";
      }
      return null;
    },

    canLoad() {
      return !!(this.senior && this.from && this.to && !this.periodError);
    },

    hasInvoices() {
      return !!(this.report && this.report.invoices.length);
    },

    categoriesWithInvoices() {
      return this.report
        ? this.report.totals.by_category.filter(
            (category) => category.invoices > 0
          )
        : [];
    },

    visibleInvoices() {
      if (!this.report) {
        return [];
      }
      return this.filter
        ? this.report.invoices.filter(
            (invoice) => invoice.primary_category === this.filter
          )
        : this.report.invoices;
    },

    params() {
      return this.report
        ? {
            patient_id: this.report.senior.patient_id,
            from: this.report.period.from,
            to: this.report.period.to,
          }
        : null;
    },
  },

  created() {
    this.searchSeniors = debounce(this.searchSeniors, 300);
    this.applyPreset("last3Months");
  },

  mounted() {
    const combobox =
      this.$refs.seniorSelect.$el.querySelector('[role="combobox"]');
    if (combobox) {
      combobox.setAttribute("aria-label", "Senior");
    }
  },

  methods: {
    onSeniorSearch(search) {
      this.seniorSearchError = null;
      if (!search || search.length < 2) {
        this.seniorOptions = [];
        return;
      }
      this.seniorSearching = true;
      this.searchSeniors(search);
    },

    searchSeniors(search) {
      http
        .post(endpoints.MEDICAL_INVOICE_SENIORS, { search })
        .then((response) => {
          this.seniorOptions = response.data;
        })
        .catch((error) => {
          this.seniorOptions = [];
          this.seniorSearchError = this.errorMessage(
            error,
            "The senior search failed. Try again."
          );
        })
        .finally(() => {
          this.seniorSearching = false;
        });
    },

    applyPreset(key) {
      const now = moment();
      const ranges = {
        thisMonth: [now.clone().startOf("month"), now],
        lastMonth: [
          now.clone().subtract(1, "month").startOf("month"),
          now.clone().subtract(1, "month").endOf("month"),
        ],
        last3Months: [now.clone().subtract(3, "months").add(1, "day"), now],
        yearToDate: [now.clone().startOf("year"), now],
      };
      this.from = ranges[key][0].format("YYYY-MM-DD");
      this.to = ranges[key][1].format("YYYY-MM-DD");
      this.activePreset = key;
    },

    loadReport() {
      if (!this.canLoad) {
        return;
      }
      this.loading = true;
      this.error = null;
      this.downloadError = null;
      this.filter = null;
      this.open = {};

      http
        .post(endpoints.MEDICAL_INVOICE_REPORT, {
          patient_id: this.senior.patient_id,
          from: this.from,
          to: this.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .catch((error) => {
          this.report = null;
          this.error = this.errorMessage(
            error,
            "Something went wrong. Check your connection and try again."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    download(kind) {
      const isBundle = kind === "bundle";
      this.downloading = kind;
      this.downloadError = null;

      http
        .post(
          isBundle
            ? endpoints.MEDICAL_INVOICE_EXPORT_BUNDLE
            : endpoints.MEDICAL_INVOICE_EXPORT_EXCEL,
          this.params,
          { responseType: "blob", timeout: 180000 }
        )
        .then((blob) => {
          this.saveBlob(blob, this.fileName(isBundle ? "zip" : "xlsx"));
        })
        .catch(async (error) => {
          this.downloadError = await this.blobErrorMessage(
            error,
            isBundle
              ? "The zip could not be prepared."
              : "The Excel file could not be prepared."
          );
        })
        .finally(() => {
          this.downloading = null;
        });
    },

    saveBlob(blob, name) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },

    fileName(extension) {
      const name = (this.report.senior.name || "Senior")
        .replace(/[^A-Za-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      return `Medical_Invoices_${name}_${this.report.period.from}_to_${this.report.period.to}.${extension}`;
    },

    toggleFilter(key) {
      this.filter = this.filter === key ? null : key;
    },

    isOpen(invoice) {
      return !!this.open[invoice.invoice_no];
    },

    toggleOpen(invoice) {
      this.$set(this.open, invoice.invoice_no, !this.open[invoice.invoice_no]);
    },

    categoryLabel(key) {
      return CATEGORY_LABELS[key] || key;
    },

    payLabel(status) {
      return PAY_LABELS[status || "not recorded"] || status;
    },

    payClass(status) {
      if (status === "paid") {
        return "mi-badge--ok";
      }
      if (status === "paid not confirmed" || status === "mixed") {
        return "mi-badge--watch";
      }
      return "mi-badge--neutral";
    },

    naira(value) {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
      }).format(value || 0);
    },

    formatDate(value) {
      return value ? moment(value, "YYYY-MM-DD").format("D MMM YYYY") : "—";
    },

    errorMessage(error, fallback) {
      if (error === "Network Error") {
        return "No connection to the server. Check your internet and try again.";
      }
      const response = error && error.response;
      if (response && response.status === 403) {
        return "You don't have access to medical invoices. Ask an admin if you need it.";
      }
      if (response && response.status === 429) {
        return "Too many requests in a short time. Wait a minute and try again.";
      }
      if (response && response.status === 422 && response.data) {
        if (response.data.errors) {
          return Object.values(response.data.errors)[0][0];
        }
        if (typeof response.data.message === "string") {
          return response.data.message;
        }
      }
      return fallback;
    },

    async blobErrorMessage(error, fallback) {
      const response = error && error.response;
      if (response && response.data instanceof Blob) {
        try {
          response.data = JSON.parse(await response.data.text());
        } catch (parseError) {
          response.data = null;
        }
      }
      return this.errorMessage(error, fallback);
    },
  },
};
</script>

<style scoped>
.mi {
  min-width: 0;
  font-family: var(--gc-font-family);
  color: var(--gc-color-text);
  padding: var(--gc-space-xl) 0 var(--gc-space-xxl);
  display: grid;
  gap: var(--gc-space-base);
  font-variant-numeric: tabular-nums;
}

.mi ::selection {
  background: var(--gc-color-primary-light);
  color: var(--gc-color-text);
}

.mi-head {
  display: grid;
  gap: var(--gc-space-xs);
  margin-bottom: var(--gc-space-sm);
}

.mi-title {
  font: var(--gc-font-display);
  color: var(--gc-color-text);
  margin: 0;
}

.mi-lede,
.mi-muted {
  color: var(--gc-color-text-secondary);
  margin: 0;
}

.mi > *,
.mi-panel,
.mi-report > * {
  min-width: 0;
}

.mi-panel {
  background: var(--gc-color-surface);
  border: 1px solid var(--gc-color-border);
  border-radius: var(--gc-radius-md);
  padding: var(--gc-space-xl);
}

.mi-filters {
  display: grid;
  grid-template-columns: minmax(260px, 1.3fr) minmax(300px, 1.6fr) auto;
  gap: var(--gc-space-xl);
  align-items: end;
}

.mi-field {
  display: grid;
  gap: var(--gc-space-sm);
  min-width: 0;
  border: 0;
  padding: 0;
  margin: 0;
}

.mi-label {
  font: var(--gc-font-label);
  letter-spacing: var(--gc-tracking-label);
  color: var(--gc-color-text-secondary);
  margin: 0;
  padding: 0;
}

.mi-sublabel {
  font: var(--gc-font-label);
  color: var(--gc-color-text-secondary);
}

.mi-option {
  display: grid;
  gap: 2px;
  padding: 2px 0;
}

.mi-option-name {
  font-weight: 600;
}

.mi-option-meta {
  font-size: 12px;
  color: var(--gc-color-text-secondary);
  white-space: normal;
}

.mi ::v-deep .vs__dropdown-toggle {
  min-height: 40px;
  border: 1px solid var(--gc-color-border);
  border-radius: var(--gc-radius-sm);
  background: var(--gc-color-surface);
}

.mi ::v-deep .vs--open .vs__dropdown-toggle,
.mi ::v-deep .vs__dropdown-toggle:focus-within {
  border-color: var(--gc-color-primary);
  box-shadow: var(--gc-focus-ring);
}

.mi ::v-deep .vs__dropdown-menu {
  border-color: var(--gc-color-border);
  box-shadow: var(--gc-shadow-overlay);
  font: var(--gc-font-body);
}

.mi ::v-deep .vs__dropdown-option {
  white-space: normal;
  padding: var(--gc-space-sm) var(--gc-space-md);
  color: var(--gc-color-text);
}

.mi ::v-deep .vs__dropdown-option--highlight {
  background: var(--gc-color-primary-light);
  color: var(--gc-color-text);
}

.mi ::v-deep .vs__clear,
.mi ::v-deep .vs__open-indicator {
  fill: var(--gc-color-text-secondary);
}

.mi ::v-deep .vs__search::placeholder {
  color: var(--gc-color-text-tertiary);
}

.mi-presets {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gc-space-sm);
}

.mi-chip {
  min-height: 32px;
  padding: 0 var(--gc-space-md);
  border: 1px solid var(--gc-color-border);
  border-radius: var(--gc-radius-full);
  background: var(--gc-color-surface);
  color: var(--gc-color-text);
  font: var(--gc-font-body);
  cursor: pointer;
  transition: background-color var(--gc-duration-state),
    border-color var(--gc-duration-state);
}

.mi-chip:hover {
  background: var(--gc-color-canvas);
}

.mi-chip.is-active {
  background: var(--gc-color-primary-light);
  border-color: var(--gc-color-primary);
  color: var(--gc-color-primary-dark);
  font-weight: 600;
}

.mi-chip-count {
  margin-left: var(--gc-space-xs);
  color: var(--gc-color-text-secondary);
}

.mi-chip.is-active .mi-chip-count {
  color: var(--gc-color-primary-dark);
}

.mi-dates {
  display: flex;
  gap: var(--gc-space-md);
}

.mi-date {
  display: grid;
  gap: var(--gc-space-xs);
  flex: 1;
  margin: 0;
}

.mi-input {
  height: 40px;
  padding: 0 var(--gc-space-md);
  border: 1px solid var(--gc-color-border);
  border-radius: var(--gc-radius-sm);
  background: var(--gc-color-surface);
  color: var(--gc-color-text);
  font: var(--gc-font-body);
}

.mi-input:hover {
  border-color: var(--gc-color-text-tertiary);
}

.mi-input:focus {
  outline: none;
  border-color: var(--gc-color-primary);
  box-shadow: var(--gc-focus-ring);
}

.mi-btn {
  height: 40px;
  padding: 0 var(--gc-space-base);
  border: 1px solid transparent;
  border-radius: var(--gc-radius-sm);
  font: var(--gc-font-title);
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color var(--gc-duration-state);
}

.mi-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.mi-btn--primary {
  background: var(--gc-color-primary);
  color: var(--gc-color-text-inverse);
}

.mi-btn--primary:not(:disabled):hover {
  background: var(--gc-color-primary-dark);
}

.mi-btn--secondary {
  background: var(--gc-color-primary-light);
  color: var(--gc-color-primary-dark);
}

.mi-btn--secondary:not(:disabled):hover {
  background: var(--gc-color-primary-muted);
}

.mi-chip:focus-visible,
.mi-btn:focus-visible,
.mi-link:focus-visible,
.mi-expand:focus-visible {
  outline: 2px solid var(--gc-color-primary);
  outline-offset: 2px;
}

.mi-error-text {
  color: var(--gc-color-risk-red-text);
  margin: 0;
  font-size: 13px;
}

.mi-state {
  display: grid;
  gap: var(--gc-space-sm);
  justify-items: start;
}

.mi-state--inline {
  padding: var(--gc-space-base) 0 0;
}

.mi-state--error {
  border-color: var(--gc-color-risk-red-bg);
  background: var(--gc-color-risk-red-bg);
}

.mi-state-title,
.mi-section-title {
  font: var(--gc-font-headline);
  color: var(--gc-color-text);
  margin: 0;
}

.mi-state p {
  color: var(--gc-color-text-secondary);
  margin: 0;
  max-width: 70ch;
}

.mi-skeleton {
  width: 100%;
  height: 20px;
  border-radius: var(--gc-radius-xs);
  background: var(--gc-color-surface-secondary);
}

.mi-skeleton:nth-child(odd) {
  width: 72%;
}

.mi-report {
  display: grid;
  gap: var(--gc-space-base);
}

.mi-report-head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--gc-space-base);
  align-items: flex-start;
}

.mi-report-title {
  font: var(--gc-font-display);
  font-size: 24px;
  line-height: 32px;
  margin: 0;
  color: var(--gc-color-text);
}

.mi-downloads {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gc-space-sm);
}

.mi-ledger-wrap,
.mi-table-wrap {
  overflow-x: auto;
}

.mi-ledger,
.mi-table,
.mi-lines {
  width: 100%;
  border-collapse: collapse;
  font: var(--gc-font-body);
  color: var(--gc-color-text);
}

.mi-ledger th,
.mi-ledger td,
.mi-table th,
.mi-table td,
.mi-lines th,
.mi-lines td {
  padding: var(--gc-space-md);
  text-align: left;
  vertical-align: top;
  border-bottom: 1px solid var(--gc-color-border-light);
}

.mi-ledger thead th,
.mi-table thead th,
.mi-lines thead th {
  font: var(--gc-font-label);
  letter-spacing: var(--gc-tracking-label);
  color: var(--gc-color-text-secondary);
  background: var(--gc-color-canvas);
  border-bottom: 1px solid var(--gc-color-border);
  white-space: nowrap;
}

.mi-ledger tbody th {
  font-weight: 400;
}

.mi-ledger tbody tr.is-selected {
  background: var(--gc-color-primary-light);
}

.mi-ledger tfoot th,
.mi-ledger tfoot td {
  font-weight: 600;
  border-bottom: 0;
  border-top: 1px solid var(--gc-color-border);
}

.mi-grand {
  font-size: 20px;
  line-height: 28px;
  color: var(--gc-color-text);
}

.num {
  text-align: right !important;
  white-space: nowrap;
}

.nowrap {
  white-space: nowrap;
}

.mono {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.mi-link {
  background: none;
  border: 0;
  padding: 0;
  color: var(--gc-color-primary);
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.mi-link[aria-pressed="true"] {
  color: var(--gc-color-primary-dark);
}

.mi-reissue-note {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gc-space-sm);
  align-items: center;
  color: var(--gc-color-text-secondary);
  margin: 0;
}

.mi-table-head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--gc-space-md);
  align-items: center;
  margin-bottom: var(--gc-space-base);
}

.mi-table tbody.is-reissue > tr:first-child {
  background: var(--gc-color-risk-yellow-bg);
}

.mi-table tbody:hover > tr:first-child {
  background: var(--gc-color-canvas);
}

.mi-table tbody.is-reissue:hover > tr:first-child {
  background: var(--gc-color-risk-yellow-bg);
}

.mi-expand {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--gc-color-border);
  border-radius: var(--gc-radius-sm);
  background: var(--gc-color-surface);
  color: var(--gc-color-text);
  cursor: pointer;
}

.mi-expand svg {
  transition: transform var(--gc-duration-state);
}

.mi-expand svg.is-open {
  transform: rotate(90deg);
}

.mi-lines-row > td {
  background: var(--gc-color-canvas);
  padding-top: 0;
}

.mi-lines {
  background: var(--gc-color-surface);
  border: 1px solid var(--gc-color-border);
  border-radius: var(--gc-radius-sm);
}

.mi-desc {
  min-width: 260px;
}

.mi-badge {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 10px;
  border-radius: var(--gc-radius-full);
  font: var(--gc-font-label);
  letter-spacing: var(--gc-tracking-label);
  white-space: nowrap;
}

.mi-badge--block {
  display: flex;
  width: max-content;
  margin-top: var(--gc-space-xs);
}

.mi-badge--ok {
  background: var(--gc-color-risk-green-bg);
  color: var(--gc-color-risk-green-text);
}

.mi-badge--watch {
  background: var(--gc-color-risk-yellow-bg);
  color: var(--gc-color-risk-yellow-text);
}

.mi-badge--neutral {
  background: var(--gc-color-surface-secondary);
  color: var(--gc-color-text);
}

.mi-notes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--gc-space-base);
  margin: var(--gc-space-xl) 0 0;
  padding-top: var(--gc-space-base);
  border-top: 1px solid var(--gc-color-border-light);
}

.mi-notes dt {
  font: var(--gc-font-label);
  color: var(--gc-color-text);
  margin-bottom: var(--gc-space-xs);
}

.mi-notes dd {
  margin: 0;
  color: var(--gc-color-text-secondary);
  font-size: 13px;
  line-height: 18px;
}

.mi-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1100px) {
  .mi-filters {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .mi-panel {
    padding: var(--gc-space-base);
  }

  .mi-dates {
    flex-direction: column;
  }
}
</style>
